import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  author = 'ESCR LLC',
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  canonicalUrl,
  structuredData,
  noindex = false
}) => {
  const location = useLocation();
  const baseUrl = 'https://escr.online';
  const fullUrl = canonicalUrl || `${baseUrl}${location.pathname}`;
  const pageTitle = title ? `${title} | ESCR LLC` : 'ESCR LLC - Software Development Company';
  const pageDescription = description || 'ESCR LLC delivers cutting-edge software solutions that transform businesses. Expert web development, mobile apps, and cloud solutions.';
  const ogTitleFinal = ogTitle || title || 'ESCR LLC - Software Development Company';
  const ogDescriptionFinal = ogDescription || description || pageDescription;
  const ogImageFinal = ogImage || `${baseUrl}/og-image.jpg`; // Update with your actual OG image

  useEffect(() => {
    // Update document title
    document.title = pageTitle;

    // Update or create meta tags
    const updateMetaTag = (name, content, attribute = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', pageDescription);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }
    updateMetaTag('author', author);
    updateMetaTag('language', 'English');
    updateMetaTag('content-language', 'en-US');
    
    // Open Graph tags
    updateMetaTag('og:title', ogTitleFinal, 'property');
    updateMetaTag('og:description', ogDescriptionFinal, 'property');
    updateMetaTag('og:image', ogImageFinal, 'property');
    updateMetaTag('og:url', fullUrl, 'property');
    updateMetaTag('og:type', ogType, 'property');
    updateMetaTag('og:site_name', 'ESCR LLC', 'property');
    
    // Twitter Card tags
    updateMetaTag('twitter:card', twitterCard);
    updateMetaTag('twitter:title', ogTitleFinal);
    updateMetaTag('twitter:description', ogDescriptionFinal);
    updateMetaTag('twitter:image', ogImageFinal);
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', fullUrl);

    // Robots meta tag - ensure proper indexing signals
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      // Explicitly allow indexing with max-snippet and max-image-preview
      updateMetaTag('robots', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    }

    // Add structured data
    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    if (structuredData) {
      // Handle both arrays and single objects
      const dataArray = Array.isArray(structuredData) ? structuredData : [structuredData];
      
      dataArray.forEach((data, index) => {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }
  }, [location.pathname, pageTitle, pageDescription, keywords, author, ogTitleFinal, ogDescriptionFinal, ogImageFinal, fullUrl, ogType, twitterCard, structuredData, noindex]);

  return null;
};

export default SEO;
